import { useState } from 'react'
import { COLLECT_COPY } from '../../lib/collectCopy'
import { SHOP } from '../../lib/hubCopy'
import {
  ITEM_KIND,
  PRACTICE_ROWS,
  practiceFree,
  practiceHandOver,
  practiceIsLow,
  practiceSave,
  type ItemKind,
  type PracticeRow,
} from '../../lib/guidePractice'

/**
 * THE PARTS A GUIDE DRAWS (moved from components/shop/ShopGuide.tsx by 339,
 * HLP-45). A guide file names one on a line of its own - `::practice-shelf` -
 * and GuideBody looks it up in GUIDE_WIDGETS. They quote the app's own copy
 * (SHOP, COLLECT_COPY) directly, and their rules are lib/guidePractice.ts.
 * The suite fails when a guide names a widget that is not here.
 */

/** Ordered, promised, collected — and the two ways out. */
function OrderFlow() {
  const t = SHOP.en
  return (
    <>
      <ol className="guide__flow">
        <li><span className="pill">Ordered</span><b>Stars come off</b>The price leaves the person’s balance the moment they order.</li>
        <li><span className="pill">{t.waiting}</span><b>Only if your shelf is empty</b>Some items take orders with no stock. The person joins a queue at your store, oldest first.</li>
        <li><span className="pill pill--live">{t.readyToCollect}</span><b>Assigned to them</b>One unit on your shelf is promised. They get a notice and a pickup code that lasts four weeks.</li>
        <li><span className="pill">{t.collected}</span><b>You scan the code</b>You confirm on your phone. The unit leaves On shelf and Assigned together.</li>
      </ol>
      <ul className="guide__exits">
        <li><span className="pill">{t.cancelled}</span><b>By the person</b>Any time before the handover. The stars go back and a promised unit is free again.</li>
        <li><span className="pill">{t.cancelled}</span><b>The code ran out</b>Nobody collected within four weeks. The stars go back and the unit goes to the next person waiting.</li>
      </ul>
    </>
  )
}

/** The Stock tab's rules in miniature, using its own refusal. */
export function PracticeShelf({ initial = PRACTICE_ROWS }: { initial?: PracticeRow[] }) {
  const [rows, setRows] = useState<PracticeRow[]>(initial)
  const [typed, setTyped] = useState<string[]>(initial.map((r) => String(r.onShelf)))
  const [log, setLog] = useState<{ text: string; ok: boolean }[]>([])
  function say(text: string, ok: boolean) {
    setLog((l) => [{ text, ok }, ...l].slice(0, 3))
  }
  function save(i: number) {
    const out = practiceSave(rows[i], typed[i])
    if (!out.ok) return say(out.message, false)
    setRows((rs) => rs.map((r, j) => (j === i ? out.row : r)))
    say(out.message, true)
  }
  function hand(i: number) {
    const next = practiceHandOver(rows[i])
    setRows((rs) => rs.map((r, j) => (j === i ? next : r)))
    setTyped((ts) => ts.map((v, j) => (j === i ? String(next.onShelf) : v)))
    say(`One collected. On shelf and Assigned both went down by one.`, true)
  }
  function reset() {
    setRows(initial)
    setTyped(initial.map((r) => String(r.onShelf)))
    setLog([])
  }
  return (
    <div className="guide__inst">
      <div className="guide__instbar">
        <span>Practice shelf · example rows, nothing here is saved</span>
        <button type="button" className="btn btn--sm" onClick={reset}>Reset</button>
      </div>
      <div className="guide__tablewrap">
        <table className="admin__table guide__shelf">
          <thead>
            <tr>
              <th scope="col">Item</th><th scope="col">Colour</th><th scope="col">Size</th>
              <th scope="col">On shelf</th><th scope="col">Assigned</th><th scope="col">Free</th>
              <th scope="col">Waiting</th><th scope="col">Reorder at</th><th scope="col"><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.item}-${r.colour}-${r.size}`}>
                <td>{r.item}</td><td>{r.colour}</td><td>{r.size}</td>
                <td>
                  <input
                    className="guide__count"
                    inputMode="numeric"
                    aria-label={`On shelf, ${r.item} ${r.colour} ${r.size}`}
                    value={typed[i]}
                    onChange={(e) => setTyped((ts) => ts.map((v, j) => (j === i ? e.target.value : v)))}
                    onKeyDown={(e) => { if (e.key === 'Enter') save(i) }}
                  />
                </td>
                <td>{r.assigned}</td>
                <td>{practiceFree(r)} {practiceIsLow(r) && <span className="pill pill--draft">Low</span>}</td>
                <td>{r.waiting || '—'}</td>
                <td>{r.reorderAt ?? '—'}</td>
                <td className="guide__rowacts">
                  <button type="button" className="btn btn--primary btn--sm" onClick={() => save(i)}>Save</button>
                  <button type="button" className="btn btn--sm" disabled={r.assigned < 1} onClick={() => hand(i)}>Hand over</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="guide__log" aria-live="polite">
        {log.length === 0
          ? <p className="guide__hint">Try it: set Navy · L to 0, which is refused because one is promised. Then set it to 5, and the three people waiting are served, oldest first.</p>
          : log.map((l, i) => <p key={i} className={l.ok ? 'guide__logok' : 'guide__logbad'}>{l.text}</p>)}
      </div>
    </div>
  )
}

type ScanState = 'ready' | 'done' | 'already' | 'expired' | 'notReady' | 'unknown' | 'error'

const SCAN_WHY: Record<ScanState, string> = {
  ready: 'The normal case. Check the name, hand it over, confirm.',
  done: 'What you see after confirming. Their phone changes at the same moment.',
  already: 'The code was used before. Hand nothing over; if they say they never got it, ask a shop administrator.',
  expired: 'Cancelled, or four weeks passed. Their stars are already back.',
  notReady: 'Still waiting for stock. Have you counted the latest delivery onto the shelf?',
  unknown: 'Usually a partial scan. Scan again; if it still fails, they reopen their order.',
  error: 'Your phone could not reach OneConetec. Move to better signal or Wi-Fi.',
}

/** What the scanned page says, in its own words (COLLECT_COPY). */
export function ScanScreens({ initial = 'ready' as ScanState }: { initial?: ScanState }) {
  const [state, setState] = useState<ScanState>(initial)
  const c = COLLECT_COPY.en
  const item = (
    <div className="guide__phoneitem">
      <b>Softshell jacket</b>
      <span>Navy · M</span>
      <span>{c.forWhom('Jamie S.')}</span>
      <span>{c.atRoom('Burnaby')}</span>
    </div>
  )
  const label: Record<ScanState, string> = {
    ready: c.readyTitle, done: c.doneTitle, already: c.alreadyTitle, expired: c.expiredTitle,
    notReady: c.notReadyTitle, unknown: c.unknownTitle, error: c.errorTitle,
  }
  const screen: Record<ScanState, React.ReactNode> = {
    ready: (<>{item}<p>{c.readyCheck}</p>
      <button type="button" className="btn btn--primary" onClick={() => setState('done')}>{c.confirm}</button>
      <p className="guide__hint">{c.noSignIn}</p></>),
    done: (<>{item}<p>{c.doneBody}</p></>),
    already: (<>{item}<p>{c.alreadyBody}</p></>),
    expired: <p>{c.expiredBody}</p>,
    notReady: (<>{item}<p>{c.notReadyBody}</p></>),
    unknown: <p>{c.unknownBody}</p>,
    error: (<><p>{c.errorBody}</p><button type="button" className="btn btn--quiet">{c.retry}</button></>),
  }
  const keys = Object.keys(label) as ScanState[]
  return (
    <div className="guide__scan">
      <div>
        <div className="chips" role="group" aria-label="What the scan says">
          {keys.map((k) => (
            <button key={k} type="button" className={`chip chip--sm${state === k ? ' chip--on' : ''}`}
              aria-pressed={state === k} onClick={() => setState(k)}>
              {label[k]}
            </button>
          ))}
        </div>
        <p className="guide__why">{SCAN_WHY[state]}</p>
      </div>
      <div className="guide__phone" aria-live="polite">
        <p className="guide__phonehead">{label[state]}</p>
        {screen[state]}
      </div>
    </div>
  )
}

function Kinds() {
  const cell = (k: ItemKind) => (
    <div className={`guide__kind${k === 'preorder' ? ' guide__kind--hi' : ''}`}>
      <b>{ITEM_KIND[k].name}</b>
      <span>{ITEM_KIND[k].line}</span>
    </div>
  )
  return (
    <div className="guide__kinds">
      <span />
      <span className="guide__axis">Open-ended</span>
      <span className="guide__axis">Closes on a date</span>
      <span className="guide__axis">Empty shelf refuses</span>
      {cell('shelf')}{cell('window')}
      <span className="guide__axis">Empty shelf takes orders</span>
      {cell('waiting')}{cell('preorder')}
    </div>
  )
}

/** `::name` in a guide file → what is drawn. The one list. */
export const GUIDE_WIDGETS: Record<string, () => React.ReactNode> = {
  'order-flow': () => <OrderFlow />,
  'practice-shelf': () => <PracticeShelf />,
  'scan-screens': () => <ScanScreens />,
  'item-kinds': () => <Kinds />,
}
